import React, { useEffect, useState } from "react";
import axios from "axios";
import { SimpleGrid, Flex, Text, Icon, Spinner, useColorModeValue } from "@chakra-ui/react";
import { MdAlbum, MdMusicNote, MdHeadsetMic, MdGroups, MdBusiness } from "react-icons/md";

const STAT_ITEMS = [
    { key: "dj_stat", label: "DJ", icon: MdAlbum, role: "artist", accent: "#9C27B0" },
    { key: "musician_stat", label: "Musiciens", icon: MdMusicNote, role: "artist", accent: "#01885A" },
    { key: "auditor_pro_stat", label: "Auditeurs", icon: MdHeadsetMic, role: "auditor", accent: "#3965FF" },
    { key: "groups_stat", label: "Groupes", icon: MdGroups, role: "groups", accent: "#FFB547" },
    { key: "entities_stat", label: "Entités", icon: MdBusiness, role: null, accent: "#707EAE" },
];

/**
 * Single statistic chip. Clickable chips (those tied to a role) jump the list
 * to the matching role tab; informational ones are static.
 * @param {Object} props - { item, value, onSelect, palette }
 * @returns {React.ReactElement}
 */
function StatChip({ item, value, onSelect, palette }) {
    const clickable = Boolean(item.role);
    return (
        <Flex direction="column" gap="2px" p="10px 12px" borderRadius="14px" bg={palette.bg}
            border="1px solid" borderColor={palette.border} cursor={clickable ? "pointer" : "default"}
            transition="all 0.15s ease" onClick={clickable ? () => onSelect(item.role) : undefined}
            _hover={clickable ? { borderColor: item.accent, transform: "translateY(-2px)" } : undefined}>
            <Flex align="center" gap="6px">
                <Icon as={item.icon} color={item.accent} boxSize="16px" />
                <Text color={palette.label} fontSize="0.68rem" fontWeight="600" textTransform="uppercase" letterSpacing="0.4px">
                    {item.label}
                </Text>
            </Flex>
            <Text color={palette.value} fontSize="1.25rem" fontWeight="800" lineHeight="1.1">{value}</Text>
        </Flex>
    );
}

/**
 * Compact statistics strip fetched once from /admin/users/stats. Gives an
 * at-a-glance overview (counts by user type) without loading any heavy
 * profile payload, and lets the admin jump to a role tab in one click.
 * @param {Object} props
 * @param {Function} props.onRoleSelect - Called with "artist" | "auditor"
 * @returns {React.ReactElement}
 */
function UsersStatsStrip({ onRoleSelect }) {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const palette = {
        bg: useColorModeValue("white", "whiteAlpha.100"),
        border: useColorModeValue("secondaryGray.200", "whiteAlpha.200"),
        label: useColorModeValue("secondaryGray.600", "secondaryGray.400"),
        value: useColorModeValue("secondaryGray.900", "white"),
    };

    useEffect(() => {
        let active = true;
        axios.get("/admin/users/stats")
            .then(({ data }) => { if (active) setStats(data); })
            .catch((e) => console.error("Error fetching user stats:", e))
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, []);

    if (loading) return <Flex justify="center" py="12px" mb="12px"><Spinner size="sm" color="brand.500" /></Flex>;
    if (!stats) return null;
    return (
        <SimpleGrid columns={{ base: 3, md: 5 }} spacing="8px" mb="14px">
            {STAT_ITEMS.map((item) => (
                <StatChip key={item.key} item={item} value={stats[item.key] ?? 0} onSelect={onRoleSelect} palette={palette} />
            ))}
        </SimpleGrid>
    );
}

export default UsersStatsStrip;
